
import React, { useState, useEffect, useContext } from 'react';
import { Company, UserRole } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';

const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>;
const LinkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>;

const PdfSettingsPage: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [leftLogo, setLeftLogo] = useState<string>('');
    const [rightLogo, setRightLogo] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    const authContext = useContext(AuthContext);
    const user = authContext?.user;
    const tenantId = user?.tenantId;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fixed: Replaced SUPER_ADMIN with PLATFORM_OWNER
                if (user?.role === UserRole.PLATFORM_OWNER) {
                    // Fixed: For Platform Owner, we might want all companies or specific tenant ones. 
                    // Using getCompanies(tenantId) for safety.
                    if (tenantId) {
                        const fetchedCompanies = await firestoreService.getCompanies(tenantId);
                        setCompanies(fetchedCompanies);
                        if (fetchedCompanies.length > 0) {
                            setSelectedCompanyId(fetchedCompanies[0].companyId);
                        }
                    }
                } else if (user?.companyId) {
                    const company = await firestoreService.getCompany(user.companyId);
                    if (company) {
                        setCompanies([company]);
                        setSelectedCompanyId(company.companyId);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch companies:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user, tenantId]);

    useEffect(() => {
        if (selectedCompanyId) {
            const company = companies.find(c => c.companyId === selectedCompanyId);
            if (company) {
                setLeftLogo(company.reportSettings?.headerLogos?.left || '');
                setRightLogo(company.reportSettings?.headerLogos?.right || '');
            } else {
                setLeftLogo('');
                setRightLogo('');
            }
        }
    }, [selectedCompanyId, companies]);

    const handleSave = async () => {
        if (!selectedCompanyId) return;
        setIsSaving(true);
        try {
            await firestoreService.updateCompany(selectedCompanyId, {
                reportSettings: {
                    headerLogos: {
                        left: leftLogo, // Send empty string if empty, do not send undefined
                        right: rightLogo
                    }
                }
            });
            
            setCompanies(prev => prev.map(c => 
                c.companyId === selectedCompanyId 
                ? { ...c, reportSettings: { headerLogos: { left: leftLogo, right: rightLogo } } }
                : c
            ));
            
            alert("PDF report settings saved successfully.");
        } catch (error) {
            console.error("Failed to save settings:", error);
            alert(`Error saving settings: ${(error as Error).message}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;

    return (
        <div className="container mx-auto max-w-4xl">
            <h1 className="text-3xl font-bold text-white mb-6">Configuración de Informes PDF</h1>
            <p className="text-gray-400 mb-8">Personaliza la cabecera de los informes PDF. Introduce las URLs de los logotipos que aparecerán en la parte superior izquierda y derecha.</p>

            <div className="bg-gray-900 rounded-lg shadow-lg p-8 border border-gray-800">
                {companies.length > 1 && (
                    <div className="mb-8">
                        <label className="block text-sm font-medium text-gray-300 mb-2">Seleccionar Empresa</label>
                        <select 
                            value={selectedCompanyId} 
                            onChange={(e) => setSelectedCompanyId(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                        >
                            {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                        </select>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* Left Logo */}
                    <div>
                        <h3 className="text-lg font-semibold text-white mb-4">Logo Izquierdo</h3>
                        <div className="mb-4">
                             <label className="block text-sm text-gray-400 mb-1">URL de la Imagen</label>
                             <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LinkIcon />
                                </div>
                                <input
                                    type="url"
                                    value={leftLogo}
                                    onChange={(e) => setLeftLogo(e.target.value)}
                                    placeholder="https://ejemplo.com/logo.png"
                                    className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                             </div>
                        </div>
                        
                        <div className="h-40 bg-gray-800/50 border border-gray-700 border-dashed rounded-lg flex items-center justify-center overflow-hidden relative">
                             {leftLogo ? (
                                <img src={leftLogo} alt="Preview Left" className="max-w-full max-h-full object-contain p-2" onError={(e) => (e.currentTarget as HTMLImageElement).style.display = 'none'} />
                            ) : (
                                <span className="text-gray-500 text-sm">Vista previa</span>
                            )}
                             {leftLogo && (
                                <div className="absolute top-2 right-2">
                                     <button onClick={() => setLeftLogo('')} className="bg-gray-900/80 text-red-400 rounded-full p-1 hover:bg-gray-700">
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                     </button>
                                </div>
                             )}
                        </div>
                    </div>

                    {/* Right Logo */}
                    <div>
                        <h3 className="text-lg font-semibold text-white mb-4">Logo Derecho</h3>
                         <div className="mb-4">
                             <label className="block text-sm text-gray-400 mb-1">URL de la Imagen</label>
                             <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LinkIcon />
                                </div>
                                <input
                                    type="url"
                                    value={rightLogo}
                                    onChange={(e) => setRightLogo(e.target.value)}
                                    placeholder="https://ejemplo.com/logo.png"
                                    className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
                                />
                             </div>
                        </div>

                         <div className="h-40 bg-gray-800/50 border border-gray-700 border-dashed rounded-lg flex items-center justify-center overflow-hidden relative">
                             {rightLogo ? (
                                <img src={rightLogo} alt="Preview Right" className="max-w-full max-h-full object-contain p-2" onError={(e) => (e.currentTarget as HTMLImageElement).style.display = 'none'} />
                            ) : (
                                <span className="text-gray-500 text-sm">Vista previa</span>
                            )}
                            {rightLogo && (
                                <div className="absolute top-2 right-2">
                                     <button onClick={() => setRightLogo('')} className="bg-gray-900/80 text-red-400 rounded-full p-1 hover:bg-gray-700">
                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                     </button>
                                </div>
                             )}
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex justify-end border-t border-gray-800 pt-6">
                    <button 
                        onClick={handleSave} 
                        disabled={isSaving}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:bg-gray-600 flex items-center"
                    >
                        {isSaving ? <Spinner size="sm" /> : <SaveIcon />}
                        Guardar Configuración
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PdfSettingsPage;
